import debounce from 'lodash/debounce';
import { useEffect, useCallback, useMemo } from 'react';

import { useAlbumStore } from '../state/albumStore';

/**
 * Hook that sends the current document height to the parent window.
 * Uses ResizeObserver to detect dimension changes more efficiently.
 */
export const useHeightMessenger = () => {
  const isShowcaseEmbedded = useAlbumStore((state) => state.isShowcaseEmbedded);
  const albumId = useAlbumStore((state) => state.albumId);

  const sendHeight = useCallback(() => {
    try {
      // Get referrer or default to '*' for any origin
      const targetOrigin = window?.document?.referrer ?? '*';
      if (targetOrigin && window.parent && window.parent !== window) {
        const height = document.documentElement.offsetHeight || document.body.offsetHeight;
        window.parent.postMessage({ showcaseHeight: height, showcaseId: albumId }, targetOrigin);
      }
    } catch (e) {
      console.error('Error posting message:', e);
    }
  }, [albumId]);

  const debouncedSendHeight = useMemo(() => debounce(sendHeight, 500), [sendHeight]);

  useEffect(() => {
    if (!isShowcaseEmbedded || typeof window === 'undefined') {
      return;
    }

    sendHeight();

    let resizeObserver: ResizeObserver | undefined;
    // Set up ResizeObserver to monitor document size changes
    try {
      resizeObserver = new ResizeObserver(() => debouncedSendHeight());
      resizeObserver.observe(document.documentElement);
    } catch (error) {
      console.error('ResizeObserver not supported, falling back to resize event', error);
      // Fallback to resize event if ResizeObserver is not supported
      window.addEventListener('resize', debouncedSendHeight);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        window.removeEventListener('resize', debouncedSendHeight);
      }
      debouncedSendHeight.cancel();
    };
  }, [isShowcaseEmbedded, debouncedSendHeight, sendHeight]);
};
